import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { initAnalytics, capturePageview } from "./analytics";
import { AnimatePresence } from "framer-motion";
import ThemeWrapper from "./ThemeWrapper";
import App from "./App";
// Self-hosted typefaces. These MUST be npm imports, never a <link> to a font
// CDN: react-snap's CSS-inlining step aborts on a cross-origin request and
// fails the entire production build on every route.
import "@fontsource-variable/fraunces";
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "./index.css";
import HomePage from "./pages/HomePage";
import StudioPage from "./pages/StudioPage";
import AboutPage from "./pages/AboutPage";
import ResumePage from "./pages/ResumePage";
import HirePage from "./pages/HirePage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import NotFoundPage from "./pages/NotFoundPage";

function AnimatedRoutes() {
  const location = useLocation();

  // A single-page app changes URL without a document load, so each route change
  // has to be reported explicitly or every session collapses to one pageview.
  useEffect(() => {
    capturePageview(location.pathname);
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<HomePage />} />
        <Route path="/studio" element={<StudioPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/resume" element={<ResumePage />} />
        <Route path="/hire" element={<HirePage />} />
        <Route path="/chat" element={<App />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AnimatePresence>
  );
}

initAnalytics();

const rootElement = document.getElementById("root");
const app = (
  <React.StrictMode>
    <BrowserRouter>
      <ThemeWrapper>
        <AnimatedRoutes />
      </ThemeWrapper>
    </BrowserRouter>
  </React.StrictMode>
);

// We render rather than hydrate, even over react-snap's pre-rendered HTML.
// react-snap pre-renders by running the app CLIENT-side in Puppeteer and
// serialising the DOM, so React's server-render text separators (`<!-- -->`)
// were never emitted — every page therefore failed hydration (React #425 →
// #418 → #423) and fell back to a full client render anyway, just noisily and
// after doing the work twice. The pre-render still earns its keep: crawlers and
// first paint get real HTML.
// ponytail: true hydration would need SSR that emits separators (Next/Vite SSG),
// not a patch here — revisit only if CWV data says the re-render costs us.
createRoot(rootElement).render(app);
